import { Suspense } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { Section } from "@roqua/components/sections";

import { BackButton } from "@/components/ui/BackButton";
import { ChooseQuestionnairesContainer } from "@/features/FillOutWizard/ChooseQuestionnairesNew";
import { MeasurementInfoQuery } from "@/features/FillOutWizard/ChooseQuestionnairesNew/graphql";
import { RespondentTabs } from "@/features/FillOutWizard/ChooseQuestionnairesNew/RespondentTabs";
import { RespondentTabsQuery } from "@/features/FillOutWizard/RespondentTabs/RespondentTabsQuery";
import { defaultRespondentType } from "@/features/FillOutWizard/Sidebar/functions";
import { RespondentTypeEnum } from "@/generated/gql/graphql";
import { zodRespondentTypeEnum } from "@/models/respondent";

const searchSchema = z.object({
  respondentType: zodRespondentTypeEnum.optional(),
});

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/fill_out_wizard_new/$measurementId/choose",
)({
  validateSearch: zodValidator(searchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const { respondentType } = Route.useSearch();

  if (!respondentType) {
    return <NavigateToDefaultRespondentType />;
  }

  return <WithRespondentType respondentType={respondentType} />;
}

function NavigateToDefaultRespondentType() {
  const { organizationKey, dossierId, measurementId } = Route.useParams();

  const {
    data: {
      organization: {
        measurement: { respondentTypes },
      },
    },
  } = useSuspenseQuery(RespondentTabsQuery, {
    variables: { organizationKey, measurementId },
  });

  return (
    <Navigate
      to={"/$organizationKey/$dossierId/fill_out_wizard_new/$measurementId/choose"}
      params={{ organizationKey, dossierId, measurementId }}
      search={{ respondentType: defaultRespondentType(respondentTypes) }}
      replace
    />
  );
}

function WithRespondentType({ respondentType }: { respondentType: RespondentTypeEnum }) {
  const { organizationKey, measurementId } = Route.useParams();

  const {
    data: {
      organization: { measurement },
    },
  } = useSuspenseQuery(MeasurementInfoQuery, {
    variables: { organizationKey, measurementId },
    fetchPolicy: "cache-first",
  });

  return (
    <>
      <Section variant="header">
        <div className="flex items-center space-x-2">
          <header className="flex-1 text-2xl">
            {measurement.protocol.name} &rarr; {measurement.name}
          </header>
          <BackButton href="/$organizationKey/$dossierId/measure" />
        </div>
      </Section>
      <Section>
        <Suspense>
          <RespondentTabs measurementId={measurementId} selectedRespondentType={respondentType} />
        </Suspense>
        <Suspense>
          <ChooseQuestionnairesContainer
            measurementId={measurementId}
            respondentType={respondentType}
          />
        </Suspense>
      </Section>
    </>
  );
}
